// src/pages/Events/GroupMessageModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getGroupsList } from "../../../services/groupServices";
import { sendGroupMessage } from "../../../services/messageServices";
import Button from "../../../components/Button/Button";
import {
  FaTimes,
  FaPaperPlane,
  FaSearch,
  FaListUl,
  FaMagic,
} from "react-icons/fa";
import formatDate from "../../../utils/formatDate";
import { toast } from "react-toastify";

const MAX_SUBJECT = 180;

const TEMPLATES = {
  invitation: {
    name: "Invitation",
    subject: (e) =>
      `[Inscription] ${e?.titre || "Événement"} — ${
        e?.start ? formatDate(e.start) : ""
      }`,
    content: (e) =>
      `Bonjour,

Merci de confirmer votre participation à {titre} le {date}{lieu}.
Veuillez vous inscrire depuis la page Calendrier.

Sportivement,`,
  },
  reminder: {
    name: "Rappel J-2",
    subject: (e) =>
      `[Rappel] ${e?.titre || "Événement"} — ${
        e?.start ? formatDate(e.start) : ""
      }`,
    content: (e) =>
      `Bonjour,

Petit rappel pour {titre} le {date}{lieu}.
Merci de valider votre présence si ce n'est pas déjà fait.

À très vite,`,
  },
  update: {
    name: "Mise à jour",
    subject: (e) =>
      `[Infos] ${e?.titre || "Événement"} — ${
        e?.start ? formatDate(e.start) : ""
      }`,
    content: (e) =>
      `Bonjour,

Informations mises à jour pour {titre} le {date}{lieu}.
Merci d'en prendre connaissance.

Sportivement,`,
  },
};

const TOKENS = [
  { key: "{titre}", label: "Titre" },
  { key: "{date}", label: "Date" },
  { key: "{lieu}", label: "Lieu" },
  { key: "{lien_agenda}", label: "Lien agenda" },
];

function replaceTokens(str, event) {
  if (!str) return "";
  const dateStr = event?.start ? formatDate(event.start) : "";
  const lieu = event?.lieu_event || event?.lieu || "";
  const lieuPart = lieu ? ` à ${lieu}` : "";

  return str
    .replaceAll("{titre}", event?.titre || "")
    .replaceAll("{date}", dateStr)
    .replaceAll("{lieu}", lieuPart)
    .replaceAll("{lien_agenda}", ""); // <- plus de lien inséré
}

const GroupMessageModal = ({ isOpen, onClose, event, senderId }) => {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [templateKey, setTemplateKey] = useState("invitation");
  const [sending, setSending] = useState(false);

  const textareaRef = useRef(null);

  // Chargement des groupes
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const g = await getGroupsList();
        setGroups(Array.isArray(g) ? g : []);
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger les groupes.");
      }
    })();
  }, [isOpen]);

  // Pré-remplissage via template
  useEffect(() => {
    if (!isOpen) return;
    const t = TEMPLATES[templateKey] || TEMPLATES.invitation;
    setSubject(t.subject(event));
    setContent(t.content(event));
  }, [isOpen, event, templateKey]);

  const groupsFiltered = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      String(g.nom || "")
        .toLowerCase()
        .includes(q)
    );
  }, [groups, groupSearch]);

  const handleInsertToken = (token) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => (prev || "") + token);
      return;
    }
    const start = textarea.selectionStart ?? (content?.length || 0);
    const end = textarea.selectionEnd ?? start;
    const before = content.slice(0, start);
    const after = content.slice(end);
    const next = before + token + after;
    setContent(next);
    // Repositionner le curseur après insertion
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + token.length;
      textarea.setSelectionRange(pos, pos);
    });
  };

  // Remplacer l'ancienne handleSend par celle-ci
  const handleSend = async () => {
    if (!groupId) return toast.error("Veuillez sélectionner un groupe.");

    // Remplacement des tokens {titre} {date} {lieu} {lien_agenda}
    const finalSubject = replaceTokens(subject, event).trim();
    const finalContent = replaceTokens(content, event).trim();

    if (!finalSubject || !finalContent)
      return toast.error("Sujet et message sont requis.");

    if (finalSubject.length > MAX_SUBJECT)
      return toast.error(
        `Sujet trop long (${finalSubject.length}/${MAX_SUBJECT}).`
      );

    setSending(true);
    try {
      await sendGroupMessage({
        sender_id: senderId,
        group_id: Number(groupId),
        subject: finalSubject,
        content: finalContent,
      });
      toast.success("Message groupé envoyé !");
      onClose?.(true);
    } catch (e) {
      console.error(e);
      toast.error("Échec de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const previewSubject = replaceTokens(subject, event);
  const previewContent = replaceTokens(content, event);

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      {/* Conteneur 85vh + scroll + header sticky */}
      <div className="relative w-full max-w-4xl h-[85vh] max-h-[85vh] overflow-y-auto rounded-2xl shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white/90 dark:bg-slate-900/80">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold">
              Invitation groupée
            </h3>
            <div className="mt-1 text-xs opacity-95 truncate">
              {event?.titre || "Événement"} ·{" "}
              {event?.start ? formatDate(event.start) : ""} ·{" "}
              {event?.lieu_event || event?.lieu || ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose?.(false)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps */}
        <div className="grid md:grid-cols-2 gap-0">
          {/* Colonne gauche : Form */}
          <div className="p-5 sm:p-7 space-y-5">
            {/* Choix Template */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FaMagic className="opacity-70" /> Modèle
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(TEMPLATES).map(([key, t]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTemplateKey(key)}
                      className={`px-3 py-1 rounded-full text-xs ring-1 transition ${
                        templateKey === key
                          ? "bg-violet-600 text-white ring-violet-600"
                          : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ring-slate-200 dark:ring-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/70"
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sélection de groupe + recherche */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
              <label className="block text-sm font-medium mb-2">Groupe</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <select
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                  >
                    <option value="">— Sélectionner un groupe —</option>
                    {groupsFiltered.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.nom}{" "}
                        {typeof g.count === "number" ? `(${g.count})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                  <input
                    placeholder="Rechercher…"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-9 pr-3 py-2"
                  />
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
                <FaListUl /> {groupsFiltered.length} groupe
                {groupsFiltered.length > 1 ? "s" : ""}
                {groupId && " • sélectionné ✓"}
              </div>
            </div>

            {/* Sujet */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
              <label className="block text-sm font-medium mb-2">Sujet</label>
              <input
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={MAX_SUBJECT}
              />
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {subject.length}/{MAX_SUBJECT}
              </div>
            </div>

            {/* Message + tokens */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="block text-sm font-medium">Message</label>
                <div className="flex flex-wrap gap-2">
                  {TOKENS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className="px-2 py-0.5 rounded-full text-xs ring-1 ring-slate-300 dark:ring-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/70"
                      onClick={() => handleInsertToken(t.key)}
                      title={`Insérer ${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                ref={textareaRef}
                className="w-full min-h-[160px] rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {content.length} caractères
              </div>
            </div>

            {/* Actions */}
            <div className="pt-1 flex gap-3 justify-end">
              <Button
                text="Fermer"
                onClick={() => onClose?.(false)}
                icon={FaTimes}
                className="gap-2"
              />
              <Button
                text={sending ? "Envoi..." : "Envoyer"}
                onClick={handleSend}
                icon={FaPaperPlane}
                className="btn-primary gap-2"
                disabled={sending}
              />
            </div>
          </div>

          {/* Colonne droite : Aperçu */}
          <div className="p-5 sm:p-7 bg-slate-50/60 dark:bg-slate-900/40 border-l border-slate-200 dark:border-slate-800">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/85 dark:bg-slate-900/70">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Aperçu du message
                </div>
                <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                  {previewSubject || "(Sujet)"}
                </div>
              </div>
              <div className="p-4">
                <pre className="whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200">
                  {previewContent || "(Message vide)"}
                </pre>
              </div>
            </div>

            {/* Récap évènement */}
            <div className="mt-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/85 dark:bg-slate-900/70 p-4 text-sm text-slate-700 dark:text-slate-200 space-y-1">
              <div className="font-semibold">{event?.titre || "—"}</div>
              <div>{event?.start ? formatDate(event.start) : "—"}</div>
              {(event?.lieu_event || event?.lieu) && (
                <div>{event.lieu_event || event.lieu}</div>
              )}
              {event?.agenda_url && (
                <div className="text-xs opacity-80 break-all">
                  Agenda : {event.agenda_url}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupMessageModal;
